import { apiClient } from '../apiClient';

export const PATHS = {
  PARTNERS_LIST: '/b2b/api/v3/partners',
} as const;

export type PartnerOrg = {
  /** Backend paying organization UUID, required by eligibility_check. */
  uuid: string;
  /** Slugified display name; URL-safe + deterministic across response orderings. */
  key: string;
  name: string;
  /** False means the next step is invite-code entry; true means an eligibility form. */
  supportsEligibilityCheck: boolean;
  /**
   * Underscore-joined field codes the eligibility form should collect. The
   * grammar in the wild: tokens (`FN`, `LN`, `DOB`, `EMPID`, `EMAIL`,
   * `GENDER`, `ZC`) joined by `_`; `_OR_` splits the string into alternatives
   * the user picks between; `_AND_` is grouping noise inside an alternative.
   * Examples: `'FN_LN_DOB'`, `'FN_LN_DOB_OR_EMPID_DOB'`, `'FN_LN_DOB_EMPID'`,
   * `'FN_LN_DOB_AND_EMPID_OR_ZC'`. Only meaningful when
   * `supportsEligibilityCheck` is true.
   */
  eligibilityCheckFields: string;
  /** Label for the EMPID input (e.g. `'BEMSID'`, `'Employee ID'`). Server-provided. */
  employeeIdDisplayName?: string;
  /** Help text for the EMPID "Where can I find this?" affordance. */
  employeeIdDescription?: string;
  /** When true, the EMPID input should accept digits only. */
  employeeIdInteger?: boolean;
  /** When present, the org uses SSO — start the flow at this URL instead of the eligibility form. */
  ssoUrl?: string;
  /** Whether the user must select a health plan before eligibility check. */
  selectHealthPlan?: boolean;
  /**
   * The matched paying-org UUID used to fetch org details (e.g. the custom
   * `fallbackMessage` shown on the not-qualified screen). Distinct from the
   * partner `uuid`; maps to the eligibility response's `webserver_org_uuid`.
   */
  webserverOrgUuid?: string;
  /** Optional paying org logo used on the eligible confirmation screen. */
  imageUrl?: string;
};

// The endpoint returns the full catalog (~700 orgs, ~300KB) in a single
// response. The `q`/`search` query params are ignored server-side, so callers
// filter client-side after fetching once.
type PartnersResponse = {
  payingOrgs: {
    uuid: string;
    name: string;
    supportsEligibilityCheck: boolean;
    eligibilityCheckFields: string;
    employeeIdDisplayName?: string;
    employeeIdDescription?: string;
    employeeIdInteger?: boolean;
    ssoUrl?: string;
    selectHealthPlan?: boolean;
    webserverOrgUuid?: string;
    imageUrl?: string;
  }[];
};

const slugify = (s: string) =>
  s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');

/**
 * Fetch the partner catalog. `isTester` maps to the `?is_tester=` query the
 * backend honors (Sparta passes `currentUser.isTester`): when true the response
 * also includes tester-only orgs — e.g. the Amwell RTE health-plan org used to
 * exercise the plan-selection flow. Defaults to false (production catalog).
 */
export async function fetchPartners(isTester = false): Promise<PartnerOrg[]> {
  const res = await apiClient.get<PartnersResponse>(PATHS.PARTNERS_LIST, {
    params: { is_tester: isTester },
  });
  // Dedupe by slug. The backend ships multiple entries per org (sometimes
  // identical-with-duplicate-uuids, sometimes with diverging
  // `supports_eligibility_check` / `eligibility_check_fields` values). When
  // two entries share a slug, prefer the one that supports the eligibility
  // check — otherwise `?org=<slug>` deep links can resolve to a misconfigured
  // duplicate and route the user to the invite-code screen instead of the
  // eligibility flow.
  const bySlug = new Map<string, PartnerOrg>();
  for (const o of res.data.payingOrgs) {
    const key = slugify(o.name);
    if (!key) continue;
    const existing = bySlug.get(key);
    // Skip only when we already have a strictly-better entry — i.e. one
    // that supports eligibility check while the incoming one doesn't.
    if (existing?.supportsEligibilityCheck && !o.supportsEligibilityCheck) continue;
    // Response item shape is PartnerOrg minus `key` (the derived slug), so a
    // spread + key covers every field.
    bySlug.set(key, { ...o, key });
  }
  return Array.from(bySlug.values()).sort((a, b) => a.name.localeCompare(b.name));
}

export function filterPartnerOrgs(query: string, orgs: PartnerOrg[]): PartnerOrg[] {
  const q = query.trim().toLowerCase();
  if (!q) return [];
  return orgs.filter((org) => org.name.toLowerCase().includes(q));
}
